/**
 * Фаза ожидания оплаты. `idle` — транзакции нет вовсе; `degraded` — транзакции
 * нет или она чужая (404/403; ручка кредитные транзакции ПОКРЫВАЕТ — ответ
 * бэка от 14.08.2026, §1, — так что это конец наблюдения, а не пробел
 * контракта); `expired` — ответа так и не дождались (см.
 * `PAYMENT_WAIT_LIMIT_MS`).
 */
export type PaymentPhase =
  'idle' | 'waiting' | 'completed' | 'canceled' | 'failed' | 'refunded' | 'degraded' | 'expired'

/**
 * Статус транзакции → фаза. Реальный контракт ручки (ответ бэка от 14.08.2026,
 * §1) — СТРОЧНЫЕ `pending` и `completed`. Значения боевого плеера
 * (`INITIALIZED`/`CANCELED`/`FAILED`/`REFUNDED`) остаются понятыми: таблица
 * транзакций одна на весь бэкенд, и терять их из-за узкого описания одной
 * ручки незачем.
 *
 * Ключи строчные, потому что нормализация регистра живёт РОВНО здесь
 * (`phaseOfStatus`) — в `transformResponse` её больше нет.
 *
 * `Map`, а не объектный литерал: у объекта есть прототип, и статус
 * `constructor`/`toString`/`__proto__` вернул бы truthy-не-фазу, мимо которой
 * `?? 'waiting'` проходит молча — то есть ровно та же оборванная оплата, что
 * лечится этим файлом, только с другого входа.
 */
const PHASE_BY_STATUS = new Map<string, PaymentPhase>([
  ['pending', 'waiting'],
  ['initialized', 'waiting'],
  ['completed', 'completed'],
  ['canceled', 'canceled'],
  ['failed', 'failed'],
  ['refunded', 'refunded'],
])

/**
 * Незнакомый статус — это «ждём», а НЕ «терминально». Молчаливая остановка
 * опроса на неизвестном значении — ровно тот баг, что был здесь до 14.08.2026:
 * реальный `pending` не находился в словаре плеера, опрос гас после первого же
 * ответа, и автор платил, не дождавшись зачисления. Ждать зря не страшно —
 * ожидание кончает `PAYMENT_WAIT_LIMIT_MS`; оборвать его зря нечем.
 */
export const phaseOfStatus = (status: string): PaymentPhase =>
  PHASE_BY_STATUS.get(status.trim().toLowerCase()) ?? 'waiting'

/** Как часто спрашиваем статус транзакции. */
export const POLL_MS = 3000

/**
 * Предел ожидания ответа ручки статуса. Сетевые `5xx` (и молчащая воронка)
 * иначе дают бесконечный опрос: спиннер «ждём подтверждения» стоял бы неделю,
 * ничего не обещая и ничего не давая. По истечении предела опрос гасим и
 * переводим экран в ручной режим — тот же, что при деградации: «обновите
 * баланс» плюс путь назад к витрине.
 */
export const PAYMENT_WAIT_LIMIT_MS = 15 * 60 * 1000

/**
 * За транзакцией ещё что-то стоит: либо её опрашивают, либо автор дооплачивает
 * во вкладке воронки. Такую покупку закрытие диалога переживает, а
 * терминальную — нет (иначе следующее открытие встречает автора старым итогом
 * вместо витрины).
 */
export const isLivePhase = (phase: PaymentPhase): boolean =>
  phase === 'waiting' || phase === 'degraded' || phase === 'expired'

/** Опроса больше нет, но платёж мог пройти — выход даёт «Обновить баланс». */
export const isManualPhase = (phase: PaymentPhase): boolean =>
  phase === 'degraded' || phase === 'expired'

/**
 * Терминальный ответ сервера важнее любых наших отметок: он пришёл — значит
 * ждать больше нечего, и «истекло»/«деградация» соврали бы поверх известного
 * исхода.
 */
export const resolvePhase = (
  answered: PaymentPhase | null,
  isDegraded: boolean,
  isExpired: boolean,
): PaymentPhase => {
  if (answered !== null && answered !== 'waiting') return answered
  if (isDegraded) return 'degraded'
  if (isExpired) return 'expired'
  return 'waiting'
}
